// Score section — pinned footer of the review details panel (Unit 21, spec §9).
// Raster grading: the score is the combined Notenpunkte (server-authoritative,
// via usePaperRatings) translated to the project's score format. A manual
// `score_override` (Notenpunkte 0–15) wins over the computed value. Per-criterion
// editing lives in RasterPanel; this is the pinned summary.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiJson } from "../api/client";
import { usePaperRatings } from "../api/hooks";
import type { Paper, Project } from "../api/types";
import { NP_MAX, formatNp, npToGrade } from "../lib/score";

import "./ScoreDisplay.css";

interface Props {
  project: Project;
  paper: Paper;
  onPatched: (paper: Paper) => void;
  onExport: () => void;
  exporting: boolean;
  isProcessing: boolean;
}

export function ScoreDisplay({ project, paper, onPatched, onExport, exporting, isProcessing }: Props) {
  const { t } = useTranslation();
  const { data } = usePaperRatings(paper.id);
  const result = data?.result ?? null;

  const overridden = paper.score_override != null;
  const finalNp = overridden ? paper.score_override : result?.final_np ?? null;
  const grade = finalNp != null ? npToGrade(finalNp) : null;
  const label = formatNp(finalNp, project.score_format).label;
  const fmt = (n: number | null | undefined) => (n == null ? "—" : Math.round(n * 10) / 10);
  // Progress bar: current Notenpunkte out of the 15 NP maximum.
  const pct = finalNp != null ? Math.round((finalNp / NP_MAX) * 100) : 0;

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideValue, setOverrideValue] = useState(overridden ? String(paper.score_override) : "");
  const [overrideSaving, setOverrideSaving] = useState(false);

  useEffect(() => {
    if (!overrideOpen) setOverrideValue(paper.score_override != null ? String(paper.score_override) : "");
  }, [paper.score_override, overrideOpen]);

  async function applyOverride(value: number | null) {
    setOverrideSaving(true);
    try {
      const updated = await apiJson<Paper>(`/api/papers/${paper.id}`, {
        method: "PATCH",
        body: JSON.stringify({ score_override: value }),
      });
      onPatched(updated);
      setOverrideOpen(false);
    } finally {
      setOverrideSaving(false);
    }
  }

  function commitOverride() {
    const n = Number(overrideValue);
    void applyOverride(Number.isFinite(n) ? Math.min(NP_MAX, Math.max(0, n)) : null);
  }

  return (
    <div className="score-section" data-tour="score-display">
      <div className="score-row">
        <span className="score-grade">{label ?? "—"}</span>
        <span className="score-pts">
          {fmt(finalNp)} {t("score.np_unit")}
          {grade != null && result?.grade_label ? ` · ${result.grade_label}` : ""}
        </span>
        <button
          type="button"
          className={"score-override-toggle" + (overridden ? " score-override-toggle--on" : "")}
          onClick={() => setOverrideOpen((v) => !v)}
          title={overridden ? t("paper_review.score_override_clear") : t("paper_review.score_override_set")}
        >
          {overridden ? t("common.manual_tag") : t("score.override_btn")}
        </button>
      </div>

      {overrideOpen && (
        <div className="score-override-form">
          <label className="score-override-label">
            {t("paper_review.score_override_value")}
            <input
              type="number"
              className="score-override-input"
              min={0}
              max={NP_MAX}
              step="1"
              value={overrideValue}
              onChange={(e) => setOverrideValue(e.target.value)}
              autoFocus
            />
            <span className="score-override-max">/ {NP_MAX} NP</span>
          </label>
          <div className="score-override-actions">
            {overridden && (
              <button
                type="button"
                className="score-override-clear"
                onClick={() => void applyOverride(null)}
                disabled={overrideSaving}
              >
                {t("paper_review.score_override_clear")}
              </button>
            )}
            <button type="button" className="score-override-cancel" onClick={() => setOverrideOpen(false)}>
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="score-override-save"
              onClick={commitOverride}
              disabled={overrideSaving}
            >
              {t("common.save")}
            </button>
          </div>
        </div>
      )}

      <div className="score-bar-track" aria-hidden="true">
        <div className="score-bar-fill" style={{ width: `${pct}%` }} />
      </div>

      <div className="score-sub">
        {t("score.raster_breakdown", {
          language: fmt(result?.language_np),
          content: fmt(result?.content_np),
        })}
        {result && !result.complete && !overridden ? ` — ${t("score.raster_incomplete")}` : ""}
      </div>
      <button
        type="button"
        className="score-export-btn"
        data-tour="export-btn"
        onClick={onExport}
        disabled={exporting || isProcessing}
      >
        {exporting ? "…" : `↓ ${t("paper_review.export_paper")}`}
      </button>
    </div>
  );
}
